import { useSyncExternalStore } from 'react'

let locks = 0
let saved = { overflow: '', gutter: '', overscroll: '' }

export function lockScroll(): () => void {
  if (locks++ === 0) {
    const html = document.documentElement
    saved = { overflow: html.style.overflow, gutter: html.style.scrollbarGutter, overscroll: document.body.style.overscrollBehavior }
    const gutter = window.innerWidth - html.clientWidth
    html.style.overflow = 'hidden'
    if (gutter > 0) html.style.scrollbarGutter = 'stable'
    document.body.style.overscrollBehavior = 'contain'
  }
  let released = false
  return () => {
    if (released) return
    released = true
    if (--locks > 0) return
    const html = document.documentElement
    html.style.overflow = saved.overflow
    html.style.scrollbarGutter = saved.gutter
    document.body.style.overscrollBehavior = saved.overscroll
  }
}

export type ModalEntry = { close: () => void }

const stack: ModalEntry[] = []
const listeners = new Set<() => void>()
let version = 0

const notify = () => {
  version += 1
  listeners.forEach((fn) => fn())
}

export function pushModal(entry: ModalEntry): () => void {
  stack.push(entry)
  notify()
  return () => {
    const i = stack.indexOf(entry)
    if (i < 0) return
    stack.splice(i, 1)
    notify()
  }
}

export const isTopModal = (entry: ModalEntry) => stack.at(-1) === entry

export function closeTopModal(): boolean {
  const top = stack.at(-1)
  if (!top) return false
  top.close()
  return true
}

const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

const getVersion = () => version
const getServerVersion = () => 0

export function useOpenModals(): number {
  useSyncExternalStore(subscribe, getVersion, getServerVersion)
  return stack.length
}

export function watchKeyboardInset(target: HTMLElement): () => void {
  const vv = window.visualViewport
  if (!vv) return () => undefined
  const apply = () => {
    target.style.setProperty('--kb', `${Math.max(0, window.innerHeight - vv.height - vv.offsetTop)}px`)
    target.style.setProperty('--vvh', `${vv.height}px`)
  }
  apply()
  vv.addEventListener('resize', apply)
  vv.addEventListener('scroll', apply)
  return () => {
    vv.removeEventListener('resize', apply)
    vv.removeEventListener('scroll', apply)
    target.style.removeProperty('--kb')
    target.style.removeProperty('--vvh')
  }
}
